import classNames from "classnames/bind";
import React from "react";
import Link from "../../Link/Link";
import { Skeleton } from "../../Skeleton";
import { MathDataDisplay } from "./MathDataDisplay";
import styles from "./MathDataDisplayWithBreakdown.module.scss";
import type {
  MathDataDisplayEquationBreakdownItemProps,
  MathDataDisplayEquationWithBreakdownProps,
  MathDataDisplayEquationsWithBreakdownProps,
  MathTermType,
} from "./types";

const cx = classNames.bind(styles);

/**
 * A Math term and associated descriptive details.
 */
const MathDataDisplayEquationBreakdownItem = ({
  math,
  description,
}: MathDataDisplayEquationBreakdownItemProps) => {
  if (!math) {
    return null;
  }

  return (
    <div className={cx(styles.termContainer)}>
      <MathDataDisplay className={cx(styles.termName)}>
        <MathDataDisplay.Group>{math}</MathDataDisplay.Group>
      </MathDataDisplay>
      <MathDataDisplay
        colorScheme="dark"
        hideUntilTypeset={"first"}
        className={cx(styles.termDescription)}
      >{`${description}`}</MathDataDisplay>
    </div>
  );
};

/**
 * A Math equation with optional breakdown of terms
 */
const MathDataDisplayEquationWithBreakdown = ({
  equation: equationDetails = null,
  className,
}: MathDataDisplayEquationWithBreakdownProps) => {
  if (!equationDetails) return null;

  const { equation, reference, terms } = equationDetails;

  return (
    <div className={cx(styles.equationContainer, className)}>
      <MathDataDisplay>
        <MathDataDisplay.Group>{equation}</MathDataDisplay.Group>
        <MathDataDisplay.Reference>{reference}</MathDataDisplay.Reference>
      </MathDataDisplay>
      {terms?.length ? (
        <>
          <h4 className={cx(styles.breakdownTitle)}>Where</h4>
          <div className={cx(styles.verticalLine)}>
            {terms
              ? terms.map((term: MathTermType, index: number) => {
                  return (
                    <MathDataDisplayEquationBreakdownItem
                      math={term.math}
                      description={term.description}
                      key={index}
                    />
                  );
                })
              : null}
          </div>
        </>
      ) : null}
    </div>
  );
};

/**
 * Math equations and their term breakdowns, if any
 */
const MathDataDisplayEquationsWithBreakdown = ({
  title,
  equations,
  sourceLink,
  type = "tex",
  isLoading = false,
  className,
  style,
}: MathDataDisplayEquationsWithBreakdownProps) => {
  if (!equations) {
    return null;
  }

  return (
    <MathDataDisplay.Root
      type={type}
      isLoading={isLoading}
    >
      <div
        className={cx(styles.container, className)}
        style={style}
      >
        <Skeleton isLoading={isLoading}>
          <div className={cx(styles.titleContainer)}>
            <h3 className={cx(styles.title)}>{title}</h3>
            {sourceLink ? (
              <Link
                label={sourceLink.name}
                anchorTagProps={{
                  href: sourceLink.url,
                  target: "_blank",
                  rel: "noopener noreferrer",
                }}
              ></Link>
            ) : null}
          </div>
        </Skeleton>
        {equations.map((equation, idx: number) => (
          <MathDataDisplayEquationWithBreakdown
            key={idx}
            equation={equation}
          />
        ))}
      </div>
    </MathDataDisplay.Root>
  );
};

export default MathDataDisplayEquationsWithBreakdown;
